import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useMemo, useRef } from "react";
import {
  CalendarCheck, Clock, MapPin, Receipt, Hotel,
  Loader2, ChevronRight, XCircle, CalendarDays,
  Printer, ArrowRightLeft, X, CreditCard, AlertTriangle,
  Moon, QrCode, Wallet, Ticket
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { bookingsApi, eventsApi, resolveMediaUrl, walletApi, type BookingListResponse } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/bookings")({
  head: () => ({
    meta: [{ title: "My Bookings — OyoBooking" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: BookingsPage,
});

const fmt = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(n);

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });

const STATUS_CONFIG: Record<string, { bg: string; text?: string; label: string }> = {
  confirmed:              { bg: "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/30", label: "Confirmed" },
  pending:                { bg: "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900/30", label: "Pending" },
  manager_approval_pending: { bg: "bg-yellow-50 text-yellow-700 border-yellow-100 dark:bg-yellow-950/30 dark:text-yellow-400 dark:border-yellow-900/30", label: "Awaiting Approval" },
  checked_in:             { bg: "bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-900/30", label: "Checked In" },
  checked_out:            { bg: "bg-slate-50 text-slate-700 border-slate-100 dark:bg-slate-900/40 dark:text-slate-400 dark:border-slate-800/40", label: "Completed" },
  cancelled:              { bg: "bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-900/30", label: "Cancelled" },
  expired:                { bg: "bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-900/30", label: "Expired" },
};

const PAY_CONFIG: Record<string, { bg: string; text?: string; label: string }> = {
  paid:     { bg: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", label: "Paid" },
  pending:  { bg: "bg-amber-500/10 text-amber-600 border-amber-500/20", label: "Unpaid" },
  failed:   { bg: "bg-rose-500/10 text-rose-600 border-rose-500/20", label: "Failed" },
  refunded: { bg: "bg-slate-500/10 text-slate-600 border-slate-500/20", label: "Refunded" },
  expired:  { bg: "bg-rose-500/10 text-rose-600 border-rose-500/20", label: "Expired" },
};

/* ── Receipt Modal ──────────────────────────────────────────── */
function ReceiptModal({ bookingId, onClose }: { bookingId: string; onClose: () => void }) {
  const [data, setData] = useState<{
    bookingRef: string; guestName: string; guestEmail: string;
    venueName: string; venueAddress: string; roomType: string | null;
    startDate: string; endDate: string; daysCount: number;
    pricePerDay: number; totalAmount: number; status: string;
    paymentStatus: string; paymentReference: string; paidAt: string | null; bookedAt: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bookingsApi.receipt(bookingId)
      .then(r => setData(r))
      .catch(() => toast.error("Failed to load receipt"))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const handlePrint = () => {
    if (!data) return;
    const w = window.open("", "_blank");
    if (!w) return;

    const rows = [
      { label: "Guest", value: data.guestName },
      { label: "Venue", value: data.venueName },
      { label: "Address", value: data.venueAddress || "N/A" },
      { label: "Room Category", value: data.roomType || "Standard" },
      { label: "Check-in", value: fmtDate(data.startDate) },
      { label: "Check-out", value: fmtDate(data.endDate) },
      { label: "Duration", value: `${data.daysCount} night${data.daysCount !== 1 ? "s" : ""}` },
      { label: "Rate per night", value: fmt(data.pricePerDay) },
      { label: "Paid on", value: data.paidAt ? new Date(data.paidAt).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A" },
    ];

    const barcodeLines = Array.from({ length: 30 }).map((_, i) => {
      const w = Math.random() > 0.5 ? 3 : 1;
      const o = Math.random() > 0.3 ? 1 : 0.4;
      return `<div style="width:${w}px;height:28px;background:#1c1917;opacity:${o};display:inline-block;"></div>`;
    }).join("");

    w.document.write(`
<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Receipt — ${data.bookingRef}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#1c1917;background:#f5f5f4;padding:24px 0}
  .page{max-width:520px;margin:0 auto;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 25px 50px -12px rgba(0,0,0,0.12)}
  .header{background:var(--primary, #700002);padding:32px 32px 40px;text-align:center;position:relative}
  .brand{color:#fff;font-size:22px;font-weight:700;letter-spacing:0.05em}
  .receipt{color:rgba(255,255,255,0.7);font-size:11px;text-transform:uppercase;letter-spacing:0.2em;margin-top:6px}
  .scallop{position:absolute;bottom:-12px;left:0;right:0;height:24px;background:radial-gradient(circle,transparent 6px,var(--primary, #700002) 6px);background-size:24px 24px;background-position:12px 0}
  .body{padding:28px 32px}
  .ref{background:#fafaf9;border:1px dashed #d6d3d1;border-radius:10px;padding:16px;text-align:center;margin-bottom:20px}
  .ref-label{font-size:10px;color:#78716c;text-transform:uppercase;letter-spacing:0.15em;margin-bottom:4px}
  .ref-value{font-family:ui-monospace,SFMono-Regular,monospace;font-weight:700;font-size:18px;color:#1c1917;letter-spacing:0.08em}
  .barcode{display:flex;justify-content:center;gap:2px;margin:8px auto 0;height:28px;width:180px}
  .row{display:flex;justify-content:space-between;align-items:flex-start;padding:10px 0;border-bottom:1px solid #f5f5f4}
  .label{font-size:12px;color:#78716c;font-weight:500}
  .value{font-size:13px;color:#292524;font-weight:600;text-align:right;max-width:60%;word-break:break-word}
  .total{background:#fafaf9;border:2px solid #292524;border-radius:12px;padding:18px;margin-top:20px;display:flex;justify-content:space-between;align-items:center}
  .total-label{font-size:12px;color:#78716c;text-transform:uppercase;letter-spacing:0.1em;font-weight:600}
  .total-value{font-size:22px;font-weight:700;color:#292524}
  .stamp-box{text-align:center;margin-top:16px}
  .stamp{display:inline-block;border:2px solid #16a34a;color:#16a34a;padding:4px 16px;border-radius:4px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.15em;transform:rotate(-3deg)}
  .footer{text-align:center;padding:20px 32px 32px;border-top:1px dashed #e7e5e4}
  .footer-text{font-size:11px;color:#a8a29e;line-height:1.6}
  @media print{body{background:#fff;padding:0}.page{box-shadow:none;margin:0;border-radius:0;max-width:100%}}
</style></head><body>
<div class="page">
  <div class="header">
    <div class="brand">OyoBooking</div>
    <div class="receipt">Official Receipt</div>
    <div class="scallop"></div>
  </div>
  <div class="body">
    <div class="ref">
      <div class="ref-label">Booking Reference</div>
      <div class="ref-value">${data.bookingRef}</div>
      <div class="barcode">${barcodeLines}</div>
    </div>
    ${rows.map(r => `<div class="row"><div class="label">${r.label}</div><div class="value">${r.value}</div></div>`).join("")}
    <div class="total">
      <div class="total-label">Total Paid</div>
      <div class="total-value">${fmt(data.totalAmount)}</div>
    </div>
    <div class="stamp-box">
      <div class="stamp">${data.paymentStatus === "paid" ? "Payment Verified" : data.paymentStatus}</div>
    </div>
  </div>
  <div class="footer">
    <div class="footer-text">
      Thank you for choosing OyoBooking.<br>
      For support, contact us at hello@adaptivefuture.com
    </div>
  </div>
</div>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 400);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-md" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        {loading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>
        ) : !data ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Receipt not available.</div>
        ) : (
          <>
            <div ref={printRef}>
              {/* Header */}
              <div className="relative bg-primary px-8 pt-8 pb-10 text-center">
                <h2 className="font-serif text-[22px] font-bold tracking-wide text-white">OyoBooking</h2>
                <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.2em] text-white/60">Official Receipt</p>
                {/* Decorative scalloped bottom edge */}
                <div className="absolute -bottom-3 left-0 right-0 h-6 bg-[radial-gradient(circle,transparent_6px,var(--primary,#700002)_6px)] bg-[length:24px_24px] bg-[position:12px_0]" />
              </div>

              {/* Body */}
              <div className="px-8 pt-6 pb-2">
                {/* Booking Reference Box */}
                <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-4 text-center">
                  <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-stone-500">Booking Reference</p>
                  <p className="mt-1 font-mono text-lg font-bold tracking-wider text-stone-900">{data.bookingRef}</p>
                  {/* Barcode visual */}
                  <div className="mx-auto mt-2 flex h-7 w-44 justify-center gap-[2px]">
                    {Array.from({ length: 30 }).map((_, i) => {
                      const isWide = Math.random() > 0.5;
                      const isOpaque = Math.random() > 0.3;
                      return (
                        <div
                          key={i}
                          className={`bg-stone-900 ${isWide ? "w-[3px]" : "w-[1px]"} ${isOpaque ? "opacity-100" : "opacity-40"}`}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* Details */}
                <div className="mt-5 space-y-0">
                  {[
                    { label: "Guest", value: data.guestName },
                    { label: "Venue", value: data.venueName },
                    { label: "Address", value: data.venueAddress || "N/A" },
                    { label: "Room Category", value: data.roomType || "Standard" },
                    { label: "Check-in", value: fmtDate(data.startDate) },
                    { label: "Check-out", value: fmtDate(data.endDate) },
                    { label: "Duration", value: `${data.daysCount} night${data.daysCount !== 1 ? "s" : ""}` },
                    { label: "Rate per night", value: fmt(data.pricePerDay) },
                    { label: "Paid on", value: data.paidAt ? new Date(data.paidAt).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A" },
                  ].map((row) => (
                    <div key={row.label} className="flex items-start justify-between border-b border-stone-100 py-2.5">
                      <span className="text-xs font-medium text-stone-500">{row.label}</span>
                      <span className="max-w-[60%] text-right text-[13px] font-semibold text-stone-800">{row.value}</span>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div className="mt-5 flex items-center justify-between rounded-xl border-2 border-stone-800 bg-stone-50 p-5">
                  <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">Total Paid</span>
                  <span className="font-display text-xl font-bold text-stone-900">{fmt(data.totalAmount)}</span>
                </div>

                {/* Paid stamp */}
                <div className="mt-4 text-center">
                  <span className="inline-block rounded border-2 border-emerald-500 px-4 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-600 -rotate-3">
                    {data.paymentStatus === "paid" ? "Payment Verified" : data.paymentStatus}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="mx-8 my-5 border-t border-dashed border-stone-200 py-5 text-center">
                <p className="text-[11px] leading-relaxed text-stone-400">
                  Thank you for choosing OyoBooking.<br />
                  For support, contact us at hello@adaptivefuture.com
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="sticky bottom-0 border-t border-stone-100 bg-white/95 px-6 py-4 backdrop-blur-sm">
              <Button onClick={handlePrint} className="w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
                <Printer className="size-4" /> Print / Save as PDF
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


/* ── Extend Modal ───────────────────────────────────────────── */
function ExtendModal({ booking, onClose, onExtended }: { booking: BookingListResponse; onClose: () => void; onExtended: (b: BookingListResponse) => void }) {
  const [newEnd, setNewEnd] = useState(booking.endDate.slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<"wallet" | "bank_transfer">("bank_transfer");
  const [walletBalance, setWalletBalance] = useState<number | null>(null);

  useEffect(() => {
    walletApi.getWallet().then(w => setWalletBalance(w.balance)).catch(() => setWalletBalance(null));
  }, []);

  // Normalize both dates to midnight so frontend and backend compute identical night counts
  const end = new Date(newEnd + "T00:00:00");
  const currentEnd = new Date(booking.endDate.slice(0, 10) + "T00:00:00");
  const msPerDay = 1000 * 60 * 60 * 24;
  const extraNights = Math.max(0, Math.round((end.getTime() - currentEnd.getTime()) / msPerDay));
  const pricePerNight = booking.pricePerNight || (booking.totalAmount / Math.max(1, booking.daysCount));
  const extraCost = pricePerNight * extraNights;

  const handleExtend = async () => {
    if (extraNights <= 0) { toast.error("New date must be after current check-out"); return; }
    setLoading(true);
    try {
      const res = await bookingsApi.extend(booking.id, newEnd, paymentMethod);
      onExtended({ ...booking, endDate: res.newEndDate, daysCount: booking.daysCount + res.additionalNights, totalAmount: res.newTotalAmount });
      if (res.dedicatedBank) {
        toast.success(res.message);
        // Show bank details for pending payment
      } else {
        toast.success(res.message);
        onClose();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Extension failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-md" onClick={onClose}>
      <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Dark header */}
        <div className="bg-primary px-6 py-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-2">
              <ArrowRightLeft className="size-4" /> Extend Stay
            </h2>
            <button onClick={onClose} className="rounded-full p-1 text-white/60 hover:text-white hover:bg-white/10 transition" aria-label="Close modal" title="Close modal">
              <X className="size-5" />
            </button>
          </div>
          <p className="mt-1 text-xs text-white/70">{booking.venueName}</p>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-xl bg-stone-50 p-4 border border-stone-100">
            <p className="text-xs font-medium uppercase tracking-wider text-stone-400">Current check-out</p>
            <p className="mt-1 text-sm font-bold text-stone-800">{fmtDate(booking.endDate)}</p>
          </div>
          <div>
            <label htmlFor="extend-check-out" className="text-sm font-semibold text-stone-700">New check-out date</label>
            <input
              id="extend-check-out"
              type="date"
              value={newEnd}
              min={booking.endDate.slice(0, 10)}
              onChange={e => setNewEnd(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-sm font-medium text-stone-800 focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
            />
          </div>
          {extraNights > 0 && (
            <div className="rounded-xl border-2 border-primary/10 bg-primary/[0.03] p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Additional nights</span>
                <span className="font-bold text-stone-800">{extraNights}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-stone-500">Extra cost</span>
                <span className="font-bold text-primary">{fmt(extraCost)}</span>
              </div>
            </div>
          )}
          {extraNights > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-stone-700">Payment method</p>
              <label className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 cursor-pointer hover:border-primary/40 transition">
                <input
                  type="radio"
                  name="extend-payment"
                  value="wallet"
                  checked={paymentMethod === "wallet"}
                  onChange={() => setPaymentMethod("wallet")}
                  className="size-4 accent-primary"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-stone-800">Pay from Wallet</p>
                  <p className="text-xs text-stone-500">{walletBalance !== null ? `Balance: ${fmt(walletBalance)}` : "Loading balance..."}</p>
                </div>
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 cursor-pointer hover:border-primary/40 transition">
                <input
                  type="radio"
                  name="extend-payment"
                  value="bank_transfer"
                  checked={paymentMethod === "bank_transfer"}
                  onChange={() => setPaymentMethod("bank_transfer")}
                  className="size-4 accent-primary"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-stone-800">Bank Transfer</p>
                  <p className="text-xs text-stone-500">Transfer to your dedicated account</p>
                </div>
              </label>
            </div>
          )}
          <Button onClick={handleExtend} disabled={loading || extraNights <= 0} className="w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
            {loading ? <Loader2 className="size-4 animate-spin" /> : <><CreditCard className="size-4" /> Pay & Extend</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── QR Modal ──────────────────────────────────────────────── */
function QrModal({ bookingId, onClose }: { bookingId: string; onClose: () => void }) {
  const [data, setData] = useState<{
    bookingRef: string; checkInToken: string; guestName: string;
    venueName: string; startDate: string; endDate: string; status: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    bookingsApi.qr(bookingId)
      .then(r => setData(r))
      .catch(() => toast.error("Failed to load QR code"))
      .finally(() => setLoading(false));
  }, [bookingId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-md" onClick={onClose}>
      <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Dark header */}
        <div className="relative bg-primary px-6 py-5 text-center">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-2">
              <QrCode className="size-4" /> Check-In QR
            </h2>
            <button onClick={onClose} className="rounded-full p-1 text-white/60 hover:text-white hover:bg-white/10 transition" aria-label="Close modal" title="Close modal">
              <X className="size-5" />
            </button>
          </div>
        </div>

        <div className="p-6 text-center">
          {loading ? <div className="flex h-40 items-center justify-center"><Loader2 className="size-5 animate-spin text-stone-400" /></div> : !data?.checkInToken ? (
            <p className="text-sm text-stone-400 py-8">QR code not available yet. Complete payment to generate your check-in code.</p>
          ) : (
            <>
              <div className="rounded-xl bg-stone-50 p-5 border border-stone-100">
                <QRCodeSVG value={data.checkInToken} size={200} className="mx-auto" />
              </div>
              <p className="mt-4 text-sm font-bold text-stone-800">{data.venueName}</p>
              <p className="text-xs text-stone-400 mt-1">{data.guestName} · {fmtDate(data.startDate)} — {fmtDate(data.endDate)}</p>
              <p className="mt-2 inline-block rounded-lg bg-stone-100 px-3 py-1 font-mono text-[11px] font-semibold text-stone-500">{data.checkInToken}</p>
              <p className="mt-4 text-xs text-stone-400">Show this QR code at reception to check in instantly.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Cancel Modal ──────────────────────────────────────────── */
function CancelModal({ booking, onClose, onCancelled }: {
  booking: BookingListResponse;
  onClose: () => void;
  onCancelled: (id: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await bookingsApi.cancel(booking.id, reason.trim() ? { reason: reason.trim() } : undefined);
      toast.success("Reservation cancelled. Refund request submitted if applicable.");
      onCancelled(booking.id);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Failed to cancel booking");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-md" onClick={onClose}>
      <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-red-700 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-red-200" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-white">Cancel Reservation</h2>
            </div>
            <button onClick={onClose} className="rounded-full p-1 text-white/60 hover:text-white hover:bg-white/10 transition" aria-label="Close modal" title="Close modal">
              <X className="size-5" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-xs font-semibold text-red-700">{booking.venueName}</p>
            <p className="mt-0.5 font-mono text-xs text-red-500">{booking.bookingRef}</p>
          </div>
          <p className="text-sm text-stone-600">Are you sure you want to cancel this reservation? {booking.paymentStatus === "paid" && <span className="font-semibold">A refund request will be automatically raised for review.</span>}</p>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-500">Reason (optional)</label>
            <textarea
              rows={3}
              className="mt-1.5 w-full rounded-xl border border-stone-200 bg-stone-50 px-3 py-2.5 text-sm text-stone-800 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none resize-none"
              placeholder="Tell us why you're cancelling…"
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 rounded-xl border border-stone-200 bg-stone-50 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-100 transition">
              Keep Booking
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className="flex-1 rounded-xl bg-red-700 py-2.5 text-sm font-semibold text-white hover:bg-red-800 transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="size-4 animate-spin" /> : <XCircle className="size-4" />}
              {loading ? "Cancelling…" : "Yes, Cancel"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Countdown Timer ───────────────────────────────────────── */
function CountdownTimer({ createdAt, paymentDeadline, durationMinutes = 30 }: { createdAt: string; paymentDeadline?: string; durationMinutes?: number }) {
  const computeRemaining = () => {
    if (paymentDeadline) {
      return Math.max(0, new Date(paymentDeadline).getTime() - Date.now());
    }
    const created = new Date(createdAt).getTime();
    const deadline = created + durationMinutes * 60 * 1000;
    return Math.max(0, deadline - Date.now());
  };

  const [remaining, setRemaining] = useState(computeRemaining);

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(computeRemaining());
    }, 1000);
    return () => clearInterval(interval);
  }, [createdAt, paymentDeadline, durationMinutes]);

  const mins = Math.floor(remaining / 60_000);
  const secs = Math.floor((remaining % 60_000) / 1000);
  const isExpired = remaining <= 0;

  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wide shadow-sm ${
      isExpired ? "bg-rose-50 text-rose-600 border-rose-200" : mins < 5 ? "bg-amber-50 text-amber-600 border-amber-200" : "bg-blue-50 text-blue-600 border-blue-200"
    }`}>
      <Clock className="size-2.5" />
      {isExpired ? "Expired" : `${mins}:${secs.toString().padStart(2, "0")} left`}
    </span>
  );
}

/* ── Complete Payment Modal ─────────────────────────────────── */

function CompletePaymentModal({ booking, onClose, onPaid }: { booking: any; onClose: () => void; onPaid: () => void }) {
  const [paymentMethod, setPaymentMethod] = useState<"wallet" | "bank_transfer">("bank_transfer");
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [bankDetails, setBankDetails] = useState<{ bankName: string; accountName: string; accountNumber: string } | null>(null);

  useEffect(() => {
    walletApi.getWallet().then(w => setWalletBalance(w.balance)).catch(() => setWalletBalance(null));
  }, []);

  const handlePay = async () => {
    setLoading(true);
    try {
      const res = await bookingsApi.pay(booking.id, paymentMethod);
      if (res.dedicatedBank) {
        setBankDetails(res.dedicatedBank);
      } else {
        toast.success("Payment successful!");
        onPaid();
        onClose();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  if (booking.isEvent) {
    const eventBank = booking.dedicatedBank;
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-md" onClick={onClose}>
        <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
          <div className="bg-primary px-6 py-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-2">
                <CreditCard className="size-4" /> Complete Event Payment
              </h2>
              <button onClick={onClose} className="rounded-full p-1 text-white/60 hover:text-white hover:bg-white/10 transition" aria-label="Close" title="Close">
                <X className="size-5" />
              </button>
            </div>
            <p className="mt-1 text-xs text-white/70">{booking.venueName}</p>
          </div>

          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between rounded-xl bg-amber-50 border border-amber-200 p-3">
              <span className="text-xs font-semibold text-amber-700">Payment window</span>
              <CountdownTimer createdAt={booking.createdAt} durationMinutes={30} />
            </div>

            {eventBank ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-3">
                <p className="text-sm font-semibold text-emerald-800">Transfer to your dedicated account</p>
                <div className="grid gap-2 text-sm text-stone-800">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Bank</p>
                    <p className="font-medium">{eventBank.bankName}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Account Name</p>
                    <p className="font-medium">{eventBank.accountName}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Account Number</p>
                    <p className="font-mono font-medium">{eventBank.accountNumber}</p>
                  </div>
                </div>
                <p className="text-xs text-emerald-700 font-medium">Transfer {fmt(booking.totalAmount)} to confirm your event ticket.</p>
                <Button onClick={() => { toast.info("We are verifying your transfer. Your ticket will remain pending until payment is confirmed."); onClose(); }} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">I Have Transferred</Button>
              </div>
            ) : (
              <div className="text-center py-6 text-stone-500 text-sm">
                No dedicated bank details found. Please contact support.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-md" onClick={onClose}>
      <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-primary px-6 py-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-2">
              <CreditCard className="size-4" /> Complete Payment
            </h2>
            <button onClick={onClose} className="rounded-full p-1 text-white/60 hover:text-white hover:bg-white/10 transition" aria-label="Close" title="Close">
              <X className="size-5" />
            </button>
          </div>
          <p className="mt-1 text-xs text-white/70">{booking.venueName} — {booking.bookingRef}</p>
        </div>

        <div className="p-6 space-y-4">
          {/* Countdown */}
          <div className="flex items-center justify-between rounded-xl bg-amber-50 border border-amber-200 p-3">
            <span className="text-xs font-semibold text-amber-700">Payment window</span>
            <CountdownTimer createdAt={booking.createdAt} paymentDeadline={booking.paymentDeadline} durationMinutes={30} />
          </div>

          {bankDetails ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-3">
              <p className="text-sm font-semibold text-emerald-800">Transfer to your dedicated account</p>
              <div className="grid gap-2 text-sm">
                <div><p className="text-[10px] text-muted-foreground uppercase">Bank</p><p className="font-medium">{bankDetails.bankName}</p></div>
                <div><p className="text-[10px] text-muted-foreground uppercase">Account Name</p><p className="font-medium">{bankDetails.accountName}</p></div>
                <div><p className="text-[10px] text-muted-foreground uppercase">Account Number</p><p className="font-mono font-medium">{bankDetails.accountNumber}</p></div>
              </div>
              <p className="text-xs text-emerald-700">Transfer {fmt(booking.totalAmount)} to confirm your reservation.</p>
              <Button onClick={() => { toast.info("We are verifying your transfer. Your booking will remain pending until payment is confirmed."); onClose(); }} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">Done</Button>
            </div>
          ) : (
            <>
              <div className="grid gap-3">
                <button type="button" onClick={() => setPaymentMethod("wallet")} className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all ${paymentMethod === "wallet" ? "border-primary bg-primary/5" : "border-border/50 hover:border-primary/30"}`}>
                  <Wallet className="size-6 text-primary" />
                  <div>
                    <p className="font-semibold text-sm">Pay with Wallet</p>
                    <p className="text-xs text-muted-foreground">{walletBalance !== null ? `Balance: ${fmt(walletBalance)}` : "Loading…"}</p>
                  </div>
                </button>
                <button type="button" onClick={() => setPaymentMethod("bank_transfer")} className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all ${paymentMethod === "bank_transfer" ? "border-primary bg-primary/5" : "border-border/50 hover:border-primary/30"}`}>
                  <CreditCard className="size-6 text-primary" />
                  <div>
                    <p className="font-semibold text-sm">Bank Transfer</p>
                    <p className="text-xs text-muted-foreground">Transfer to your dedicated virtual account</p>
                  </div>
                </button>
              </div>
              {paymentMethod === "wallet" && walletBalance !== null && walletBalance < booking.totalAmount && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">Insufficient wallet balance.</div>
              )}
              <Button onClick={handlePay} disabled={loading || (paymentMethod === "wallet" && walletBalance !== null && walletBalance < booking.totalAmount)} className="w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
                {loading ? <Loader2 className="size-4 animate-spin" /> : <><CreditCard className="size-4" /> Pay {fmt(booking.totalAmount)}</>}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function BookingCard({ b, onReceipt, onExtend, onQr, onCancel, onCancelEvent, onPay, onViewPayment }: {
  b: BookingListResponse & { isEvent?: boolean; eventId?: string; ticketCode?: string; quantity?: number; tierName?: string; registrationId?: string };
  onReceipt: (id: string) => void;
  onExtend: (b: BookingListResponse) => void;
  onQr: (id: string) => void;
  onCancel: (b: BookingListResponse) => void;
  onCancelEvent: (id: string) => void;
  onPay: (b: BookingListResponse) => void;
  onViewPayment: (b: BookingListResponse & { isEvent?: boolean }) => void;
}) {
  const start = new Date(b.startDate);
  const end   = new Date(b.endDate);
  const sConf = STATUS_CONFIG[b.status] ?? STATUS_CONFIG.pending;
  const pConf = PAY_CONFIG[b.paymentStatus] ?? PAY_CONFIG.pending;
  const isPaid = b.paymentStatus === "paid";
  const deadlineMs = b.paymentDeadline ? new Date(b.paymentDeadline).getTime() : 0;
  const pastDeadline = deadlineMs > 0 && deadlineMs < Date.now();
  const isExpired = b.status === "expired" || b.paymentStatus === "expired" || (b.status === "pending" && pastDeadline);
  const canExtend = isPaid && ["confirmed", "checked_in"].includes(b.status);
  const canCancel = ["pending", "confirmed"].includes(b.status) && !isExpired;
  const isUnpaid = !isPaid && b.status !== "cancelled" && !isExpired;
  const isEventPending = b.isEvent && b.paymentStatus !== "paid" && !isExpired;

  return (
    <div
      className={`group overflow-hidden rounded-xl border border-border/80 bg-card shadow-soft transition-all duration-300 hover:shadow-elegant hover:-translate-y-0.5 hover:border-primary/20 ${isUnpaid ? "cursor-pointer" : ""}`}
      onClick={isUnpaid ? () => onViewPayment(b) : undefined}
    >
      {/* Image Section with Overlays */}
      <div className="relative h-24 sm:h-28 w-full overflow-hidden">
        {b.venueImage ? (
          <img
            src={resolveMediaUrl(b.venueImage)}
            alt={b.venueName}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-accent/40">
            {b.isEvent ? (
              <Ticket className="size-5 sm:size-7 text-muted-foreground/30" />
            ) : (
              <Hotel className="size-5 sm:size-7 text-muted-foreground/30" />
            )}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Absolute Status Badges */}
        <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 flex items-center gap-0.5 sm:gap-1 flex-wrap justify-end max-w-[70%]">
          {isUnpaid && !b.isEvent && (
            <CountdownTimer createdAt={b.createdAt} paymentDeadline={b.paymentDeadline} />
          )}
          {isEventPending && (
            <CountdownTimer createdAt={b.createdAt} durationMinutes={30} />
          )}
          <span className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 sm:px-2 py-px sm:py-0.5 text-[7px] sm:text-[9px] font-semibold tracking-wide shadow-sm backdrop-blur-md ${sConf.bg}`}>
            {sConf.label}
          </span>
          {b.paymentStatus && (
            <span className={`inline-flex items-center rounded-full border px-1.5 sm:px-2 py-px sm:py-0.5 text-[7px] sm:text-[8px] font-semibold tracking-wide shadow-sm backdrop-blur-md ${pConf.bg}`}>
              {pConf.label}
            </span>
          )}
          {b.isEvent && (
            <span className="hidden sm:inline-flex items-center rounded-full border border-primary/20 bg-primary/10 text-primary px-1.5 sm:px-2 py-px sm:py-0.5 text-[7px] sm:text-[8px] font-semibold tracking-wide shadow-sm backdrop-blur-md">
              Event Ticket
            </span>
          )}
        </div>

        {/* Venue Information Overlay */}
        <div className="absolute bottom-1.5 left-2 right-2 sm:bottom-2 sm:left-3 sm:right-3">
          <h3 className="truncate font-display text-[10px] sm:text-xs font-bold text-white drop-shadow-md">{b.venueName}</h3>
          {b.venueLocation && (
            <p className="mt-0.5 hidden sm:flex items-center gap-1 text-[9px] sm:text-[10px] text-white/85">
              <MapPin className="size-2 sm:size-2.5 text-primary" /> {b.venueLocation}
            </p>
          )}
        </div>
      </div>

      {/* Card Body */}
      <div className="p-2 sm:p-3">
        {/* Date / Time Details */}
        <div className="flex items-center flex-wrap gap-1 sm:gap-1.5 text-[8px] sm:text-[10px] text-muted-foreground">
          {b.isEvent ? (
            <span className="flex items-center gap-0.5 sm:gap-1 rounded-md bg-accent/40 px-1.5 sm:px-2 py-0.5 sm:py-1 font-medium">
              <CalendarDays className="size-3 text-muted-foreground/80" />
              {start.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
            </span>
          ) : (
            <>
              <span className="flex items-center gap-0.5 sm:gap-1 rounded-md bg-accent/40 px-1.5 sm:px-2 py-0.5 sm:py-1 font-medium">
                <CalendarDays className="size-3 text-muted-foreground/80" />
                {start.toLocaleDateString("en-NG", { day: "numeric", month: "short" })} — {end.toLocaleDateString("en-NG", { day: "numeric", month: "short" })}
              </span>
              <span className="flex items-center gap-0.5 sm:gap-1 rounded-md bg-accent/40 px-1.5 sm:px-2 py-0.5 sm:py-1 font-medium">
                <Moon className="size-3 text-muted-foreground/80" />
                {b.daysCount} night{b.daysCount !== 1 ? "s" : ""}
              </span>
            </>
          )}
        </div>

        {b.isEvent && b.tierName && (
          <p className="mt-1 sm:mt-1.5 text-[8px] sm:text-[10px] font-semibold text-stone-700">
            {b.quantity}x {b.tierName}
          </p>
        )}

        {/* Total Cost & Reference Details */}
        <div className="mt-1.5 sm:mt-2 flex items-end justify-between">
          <div>
            <p className="text-[7px] sm:text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Total</p>
            <p className="font-display text-xs sm:text-sm font-bold text-primary">{fmt(b.totalAmount)}</p>
          </div>
          <div className="text-right">
            <p className="text-[7px] sm:text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
              {b.isEvent ? "Code" : "Ref"}
            </p>
            <p className="font-mono text-[8px] sm:text-[10px] font-semibold text-foreground/80">
              {b.bookingRef || b.ticketCode}
            </p>
          </div>
        </div>

        {/* Separator */}
        <div className="my-1.5 sm:my-2 border-t border-dashed border-border" />

        {/* Interactive Action Buttons */}
        <div className="flex flex-wrap gap-1" onClick={e => e.stopPropagation()}>
          {b.isEvent ? (
            <div className="flex flex-col sm:flex-row gap-1 w-full">
              {isEventPending && (
                <Button
                  onClick={() => onPay(b)}
                  className="flex-1 gap-1 rounded-md sm:rounded-lg py-1 sm:py-1.5 text-[8px] sm:text-[10px] font-semibold bg-amber-500 text-white hover:bg-amber-600 shadow-sm h-auto"
                >
                  <CreditCard className="size-2.5 sm:size-3" /> Pay
                </Button>
              )}
              {b.eventId && (
                <Link to={`/events/${b.eventId}/tickets`} className="flex-1 text-center">
                  <Button variant={isEventPending ? "outline" : "default"} className={`w-full gap-1 rounded-md sm:rounded-lg py-1 sm:py-1.5 text-[8px] sm:text-[10px] font-semibold shadow-sm h-auto ${isEventPending ? "" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
                    <Ticket className="size-2.5 sm:size-3" /> Tickets
                  </Button>
                </Link>
              )}
              {isPaid && b.registrationId && (
                <Button
                  onClick={() => onCancelEvent(b.registrationId!)}
                  variant="outline"
                  className="flex-1 gap-1 rounded-md sm:rounded-lg py-1 sm:py-1.5 text-[8px] sm:text-[10px] font-semibold text-destructive transition-colors hover:bg-destructive/5 hover:text-destructive hover:border-destructive/30 h-auto"
                >
                  <XCircle className="size-2.5 sm:size-3" /> Cancel
                </Button>
              )}
            </div>
          ) : (
            <>
              {isPaid && (
                <Button
                  onClick={() => onReceipt(b.id)}
                  variant="outline"
                  className="flex-1 gap-1 rounded-md sm:rounded-lg py-1 sm:py-1.5 text-[8px] sm:text-[10px] font-semibold text-foreground/80 transition-colors hover:bg-accent h-auto"
                >
                  <Receipt className="size-3" /> Receipt
                </Button>
              )}
              {isUnpaid && (
                <Button
                  onClick={() => onPay(b)}
                  className="flex-1 gap-1 rounded-md sm:rounded-lg py-1 sm:py-1.5 text-[8px] sm:text-[10px] font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm h-auto"
                >
                  <CreditCard className="size-3" /> Pay Now
                </Button>
              )}
              {canExtend && (
                <Button
                  onClick={() => onExtend(b)}
                  variant="outline"
                  className="flex-1 gap-1 rounded-md sm:rounded-lg py-1 sm:py-1.5 text-[8px] sm:text-[10px] font-semibold text-foreground/80 transition-colors hover:bg-accent h-auto"
                >
                  <ArrowRightLeft className="size-3" /> Extend
                </Button>
              )}
              {canCancel && (
                <Button
                  onClick={() => onCancel(b)}
                  variant="outline"
                  className="flex-1 gap-1 rounded-md sm:rounded-lg py-1 sm:py-1.5 text-[8px] sm:text-[10px] font-semibold text-destructive transition-colors hover:bg-destructive/5 hover:text-destructive hover:border-destructive/30 h-auto"
                >
                  <XCircle className="size-3" /> Cancel
                </Button>
              )}
              {isPaid && (b.status === "confirmed" || b.status === "checked_in") && (
                <Button
                  onClick={() => onQr(b.id)}
                  className={`flex-1 gap-1 rounded-md sm:rounded-lg py-1 sm:py-1.5 text-[8px] sm:text-[10px] font-semibold transition-all h-auto ${
                    b.status === "checked_in"
                      ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm"
                      : "bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm"
                  }`}
                >
                  <QrCode className="size-3.5" /> {b.status === "checked_in" ? "Checked In" : "Check-In"}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
function BookingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<BookingListResponse[]>([]);
  const [eventBookings, setEventBookings] = useState<any[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [tab, setTab]           = useState<"pending" | "upcoming" | "history">("upcoming");
  const [receiptId, setReceiptId] = useState<string | null>(null);
  const [extendBooking, setExtendBooking] = useState<BookingListResponse | null>(null);
  const [qrBookingId, setQrBookingId] = useState<string | null>(null);
  const [cancelBooking, setCancelBooking] = useState<BookingListResponse | null>(null);
  const [payBooking, setPayBooking] = useState<BookingListResponse | null>(null);

  const handleViewPayment = (b: BookingListResponse & { isEvent?: boolean }) => {
    if (b.isEvent) {
      setPayBooking(b);
      return;
    }
    navigate({
      to: "/booking/success",
      search: {
        venue: b.venueName,
        ref: b.bookingRef,
        total: b.totalAmount,
        bookingId: b.id,
        paymentStatus: b.paymentStatus,
        bankName: "",
        accountName: "",
        accountNumber: "",
        paymentDeadline: b.paymentDeadline ?? "",
      },
    });
  };

  const handleCancelEvent = async (id: string) => {
    if (!confirm("Cancel this event registration? The paid amount will be refunded to your wallet.")) return;
    try {
      await eventsApi.cancelRegistration(id);
      toast.success("Event registration cancelled. Refund credited to your wallet.");
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Failed to cancel registration");
    }
  };

  const load = () => {
    if (!user?.id) return;
    setLoading(true);
    Promise.all([
      bookingsApi.myBookings(user.id),
      eventsApi.getMyRegistrations().catch(() => [])
    ])
      .then(([bookingsRes, eventsRes]) => {
        setBookings(bookingsRes.data);
        setEventBookings(Array.isArray(eventsRes) ? eventsRes : []);
      })
      .catch(err => setError(err instanceof Error ? err.message : "Could not load bookings"))
      .finally(() => setLoading(false));
  };


  useEffect(() => { load(); }, [user?.id]);

  // Keep a ref of the latest bookings so the polling interval can check pending status
  // without depending on `bookings` (which would reset the interval on every poll cycle).
  const bookingsRef = useRef(bookings);
  useEffect(() => { bookingsRef.current = bookings; }, [bookings]);

  // Poll bookings while there are pending venue bookings waiting for bank-transfer webhooks.
  // The interval starts on mount and self-terminates once no pending bookings remain.
  // On the first tick (15s after mount), load() has already populated bookings, so the
  // pending check is accurate. If no pending bookings exist, the interval clears itself.
  useEffect(() => {
    if (!user?.id) return;

    const interval = setInterval(() => {
      if (!user?.id) return;
      const hasPending = bookingsRef.current.some(b => b.status === "pending" && b.paymentStatus === "pending");
      if (!hasPending) {
        clearInterval(interval);
        return;
      }
      bookingsApi.myBookings(user.id)
        .then(res => setBookings(res.data))
        .catch(() => {});
    }, 15000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const allBookings = useMemo(() => {
    const mappedVenueBookings = bookings.map(b => ({ ...b, isEvent: false }));
    const mappedEventBookings = eventBookings.map(eb => {
      // Check if this pending event booking has expired (30-minute window)
      const createdTime = new Date(eb.createdAt || eb.eventDate).getTime();
      const isExpired = eb.paymentStatus !== "paid" && (Date.now() - createdTime > 30 * 60 * 1000);
      const computedPaymentStatus = isExpired ? "expired" : eb.paymentStatus;
      const computedStatus = isExpired ? "expired" : (eb.paymentStatus === "paid" ? "confirmed" : "pending");

      return {
        id: eb.id,
        venueName: eb.eventName,
        venueLocation: eb.eventLocation,
        venueImage: undefined,
        startDate: eb.eventDate,
        endDate: eb.eventDate,
        daysCount: 1,
        totalAmount: eb.totalAmount,
        paymentStatus: computedPaymentStatus,
        status: computedStatus,
        bookingRef: eb.ticketCode || "TICKET",
        isEvent: true,
        registrationId: eb.id,
        eventId: eb.eventId,
        ticketCode: eb.ticketCode,
        quantity: eb.quantity,
        tierName: eb.tierName,
        createdAt: eb.createdAt || eb.eventDate,
        dedicatedBank: eb.dedicatedBank,
        paymentInstruction: eb.paymentInstruction,
      };
    });
    return [...mappedVenueBookings, ...mappedEventBookings].sort(
      (a, b) => new Date(b.createdAt || b.startDate).getTime() - new Date(a.createdAt || a.startDate).getTime()
    );
  }, [bookings, eventBookings]);

  const pending = useMemo(() =>
    allBookings.filter(b => b.paymentStatus !== "paid" && b.status !== "cancelled" && b.status !== "expired"),
    [allBookings]);

  const upcoming = useMemo(() =>
    allBookings.filter(b => b.paymentStatus === "paid" && !["checked_out", "cancelled", "expired"].includes(b.status)),
    [allBookings]);

  const history = useMemo(() =>
    allBookings.filter(b => ["checked_out", "cancelled", "expired"].includes(b.status)),
    [allBookings]);


  const active = tab === "pending" ? pending : tab === "upcoming" ? upcoming : history;

  return (
    <>
      <div className="space-y-5">

        {/* Page header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">My Bookings</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage reservations, view receipts, extend stays or check in.
            </p>
          </div>
          <Link
            to="/hotels"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-xs font-bold text-primary-foreground shadow transition-all hover:bg-primary/90 active:scale-95"
          >
            Browse Venues <ChevronRight className="size-3.5" />
          </Link>
        </div>

        {/* Tabs & Bookings */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div className="flex gap-1 rounded-xl border border-border/80 bg-accent/40 p-1">
              {(["pending", "upcoming", "history"] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`rounded-lg px-4.5 py-1.5 text-xs font-bold capitalize transition-all duration-200 ${
                    tab === t
                      ? "bg-background shadow-soft text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t === "pending" ? `Pending (${pending.length})` : t === "upcoming" ? `Upcoming (${upcoming.length})` : `History (${history.length})`}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex h-36 items-center justify-center gap-2 text-xs text-stone-400">
              <Loader2 className="size-4 animate-spin" /> Loading…
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-center text-xs text-red-600">
              {error}
            </div>
          ) : active.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 py-14 text-center">
              {tab === "pending"
                ? <Clock className="mb-3 size-10 text-stone-200" />
                : tab === "upcoming"
                ? <CalendarCheck className="mb-3 size-10 text-stone-200" />
                : <Receipt className="mb-3 size-10 text-stone-200" />
              }
              <p className="text-sm font-semibold text-stone-700">
                {tab === "pending" ? "No pending bookings" : tab === "upcoming" ? "No upcoming bookings" : "No booking history yet"}
              </p>
              <p className="mt-1 max-w-xs text-xs text-stone-400">
                {tab === "pending"
                  ? "You have no unpaid bookings. Great!"
                  : tab === "upcoming"
                  ? "Ready for your next stay? Browse our venues."
                  : "Your completed bookings will appear here."
                }
              </p>
              {(tab === "upcoming" || tab === "pending") && (
                <Link to="/hotels" className="mt-4 rounded-full bg-primary px-5 py-2 text-xs font-bold text-white shadow transition hover:bg-primary/90">
                  Browse Hotels
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3">
              {active.map(b => (
                <BookingCard
                  key={b.id}
                  b={b}
                  onReceipt={id => setReceiptId(id)}
                  onExtend={b => setExtendBooking(b)}
                  onQr={id => setQrBookingId(id)}
                  onCancel={b => setCancelBooking(b)}
                  onCancelEvent={id => handleCancelEvent(id)}
                  onPay={b => setPayBooking(b)}
                  onViewPayment={handleViewPayment}
                />
              ))}
            </div>
          )}

        </div>
      </div>

      {receiptId && (
        <ReceiptModal bookingId={receiptId} onClose={() => setReceiptId(null)} />
      )}
      {extendBooking && (
        <ExtendModal
          booking={extendBooking}
          onClose={() => setExtendBooking(null)}
          onExtended={(updated) => {
            setBookings(prev => prev.map(b => b.id === updated.id ? updated : b));
          }}
        />
      )}
      {qrBookingId && (
        <QrModal bookingId={qrBookingId} onClose={() => setQrBookingId(null)} />
      )}
      {cancelBooking && (
        <CancelModal
          booking={cancelBooking}
          onClose={() => setCancelBooking(null)}
          onCancelled={(id) => {
            setBookings(prev => prev.map(b => b.id === id ? { ...b, status: "cancelled" } : b));
          }}
        />
      )}
      {payBooking && (
        <CompletePaymentModal
          booking={payBooking}
          onClose={() => setPayBooking(null)}
          onPaid={() => {
            if (!payBooking.isEvent) {
              setBookings(prev => prev.map(b => b.id === payBooking.id ? { ...b, paymentStatus: "paid", status: "manager_approval_pending" } : b));
            }
          }}
        />
      )}
    </>
  );
}
